import React from "react";
import { useTranslation } from "react-i18next";
import { TextInput } from "@/components/common/text-input";
import { OutlineButton } from "@/components/common/outline-button";
import { Upload } from "@/components/common/upload";
import { IconButton } from "@/components/common/icon-button";
import { TruncatedText } from "@/components/common/truncated-text";
import { cn } from "@/utils/cn";
import {
  UploadIcon,
  TrashIcon,
} from "./assets";
import { AudioFileIcon } from "@/components/custom-audio/assets/assets/audio-file-icon";
import { AUDIO_ALLOWED_TYPES, AUDIO_MAX_FILE_SIZE } from "./constants";
import { CreditConsumeButton } from "../common/credit-consume-button";
import { SPEND_CREDITS_EVENT } from "@/manager/manager/subscription-manager";
import { UploadErrorReason } from "@/hooks/use-upload";

interface UploadContentProps {
  className?: string;
  onUpload: (file: File) => void;
  onInvalid: (reason?: UploadErrorReason) => void;
}

export const UploadContent: React.FC<UploadContentProps> = ({
  className,
  onUpload,
  onInvalid,
}) => {
  const { t } = useTranslation();
  return (
    <Upload
      className={cn(
        "py-4 flex-1 flex flex-col items-center justify-center rounded-2xl w-full text-center transition-colors cursor-pointer",
        "border border-dashed border-[rgba(208,213,221,1)] hover:border-[rgba(102,112,133,1)]",
        "bg-[rgba(249,250,251,1)]",
        className
      )}
      dragOverClassName="border-i-primary bg-blue-50"
      acceptedFileTypes={AUDIO_ALLOWED_TYPES.join(",")}
      maxFileSize={AUDIO_MAX_FILE_SIZE}
      onUpload={onUpload}
      onInvalid={onInvalid}
    >
      <div className="mb-3 flex justify-center items-center border border-[rgba(213,215,218,1)] w-10 h-10 bg-white rounded-sm">
        <UploadIcon className="w-5 h-5" />
      </div>
      <p className="mb-1 space-x-1">
        <span className="text-sm text-[rgba(117,66,247,1)] font-semibold">
          {t("custom_digital_human.upload_form.click_to_upload")}
        </span>
        <span className="text-sm text-[rgba(52,64,84,1)] font-medium">
          {t("custom_digital_human.upload_form.drag_drop")}
        </span>
      </p>
      <p className="text-xs text-[rgba(102,112,133,1)]">
        {t("custom_digital_human.upload_form.upload_audio_tips")}
      </p>
    </Upload>
  );
};

interface UploadedContentProps {
  className?: string;
  fileName: string;
  fileSize: number;
  uploadType: "audio" | "video";
  progress: number; // 0-100
  onRemove?: () => void;
}

export const UploadedContent: React.FC<UploadedContentProps> = ({
  className,
  fileName,
  fileSize,
  uploadType,
  progress,
  onRemove,
}) => {
  // 格式化文件大小
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  return (
    <div
      className={cn(
        "flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg",
        className
      )}
    >
      {/* 文件图标 */}
      <div className="flex-shrink-0">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center">
          <AudioFileIcon />
        </div>
      </div>

      {/* 文件信息 */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <TruncatedText
            className="text-sm text-[rgba(16,24,40,1)] font-medium"
            text={fileName}
          />
          {uploadType === "audio" && (
            <IconButton
              icon={
                <TrashIcon className="w-4 h-4 text-gray-400 hover:text-gray-600" />
              }
              title="Remove file"
              onClick={onRemove}
            />
          )}
        </div>

        <div className="flex items-center gap-2 text-sm text-[rgba(102,112,133,1)]">
          <span>{formatFileSize(fileSize)}</span>
          {progress < 100 && (
            <>
              <span>of</span>
              <span>{formatFileSize(fileSize)}</span>
            </>
          )}
          {/* {progress === 100 && (
            <div className="flex justify-center items-center gap-1 text-green-600">
              <CheckCircleIcon className="w-4 h-4" />
              <span className="text-sm text-[rgba(7,148,85,1)] font-medium">
                Complete
              </span>
            </div>
          )} */}
        </div>

        {/* <div className="flex items-center justify-center gap-3">
          <div className="w-full h-2 bg-[rgba(234,236,240,1)] rounded-full">
            <div
              className={cn(
                "h-2 bg-[rgba(117,66,247,1)] rounded-full transition-all duration-300"
              )}
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-sm text-[rgba(52,64,84,1)] font-medium">
            {progress}%
          </span>
        </div> */}
      </div>
    </div>
  );
};

interface UploadStepProps {
  file: File | null;
  name: string;
  uploadType: "audio" | "video";
  progress: number;
  isAllowStep?: boolean;
  errorMessage?: string;
  onUpload: (file: File | null) => void;
  onInvalid: (reason?: UploadErrorReason) => void;
  onRemove: () => void;
  onChange: (name: string) => void;
  onSubmit: (data: { spendCredits: number }) => void;
  onClose: () => void;
}

export const UploadStep = ({
  file,
  name = "",
  uploadType,
  progress,
  onUpload,
  onInvalid,
  onRemove,
  onChange,
  onSubmit,
  onClose,
  errorMessage,
  isAllowStep = true,
}: UploadStepProps) => {
  const { t } = useTranslation();
  const canCreate = file && progress === 100 && name.trim() && isAllowStep;
  return (
    <div className="flex-1 flex flex-col px-14">
      {file ? (
        <UploadedContent
          className="mb-6"
          fileName={file.name}
          fileSize={file.size}
          progress={progress}
          uploadType={uploadType}
          onRemove={() => onRemove()}
        />
      ) : (
        <UploadContent
          className="mb-6"
          onUpload={onUpload}
          onInvalid={onInvalid}
        />
      )}
      <div className="flex flex-col mb-8 gap-3">
        <label className="text-sm text-[rgba(102,112,133,1)] font-medium">
          {t("custom_digital_human.upload_form.voice_name")}
        </label>
        <TextInput
          type="text"
          value={name}
          placeholder={t("custom_digital_human.upload_form.voice_name_placeholder")}
          onChange={(e) => onChange(e.target.value)}
          helperText={errorMessage}
          error={Boolean(errorMessage)}
        />
      </div>
      <div className="mb-8 flex justify-center items-center gap-3.5">
        <OutlineButton className="w-64" label={t("common.cancel")} onClick={onClose} />
        <CreditConsumeButton
          className="w-64"
          event={SPEND_CREDITS_EVENT.miuta_audio_clone_minimax}
          label={t("common.create")}
          disabled={!canCreate}
          onClick={({ spendCredits }) => onSubmit({ spendCredits })}
        />
      </div>
    </div>
  );
};
